import React, { useState, useEffect } from "react";
import { useNavigationEvent } from "navigation-react";
import defaultlogo from "./images/logo.png";
import slugify from "./slugify";
const API_URL = "https://proseppos.salcommsystems.click/product-list-api";
const API_BASE = "https://proseppos.salcommsystems.click";
const FALLBACK_LOGO = defaultlogo

const PRODUCTS_PER_PAGE = 4;

function TopSellers() {
  const { stateNavigator } = useNavigationEvent();

  const [products, setProducts] = useState([]);
  const [activeCategory, setActiveCategory] = useState("");
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(API_URL)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load products");
        return res.json();
      })
      .then((data) => {
        setProducts(data);

        // Default = first category alphabetically (matches sidebar order)
        const cats = [
          ...new Set(data.map((p) => p.field_category).filter(Boolean)),
        ].sort();

        if (cats.length > 0) setActiveCategory(cats[0]);

        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, []);

  const categories = [
    ...new Set(products.map((p) => p.field_category).filter(Boolean)),
  ].sort();

  const currentProducts = products.filter(
    (p) => p.field_category === activeCategory
  );

  const totalPages = Math.ceil(currentProducts.length / PRODUCTS_PER_PAGE);

  useEffect(() => {
    setPage(0);
  }, [activeCategory]);

  const goTo = (newPage) => {
    const clamped = Math.max(0, Math.min(newPage, totalPages - 1));
    setPage(clamped);
  };

  const getImageUrl = (product) =>
    product.field_product_image
      ? `${API_BASE}${product.field_product_image}`
      : FALLBACK_LOGO;

  const isFallback = (product) => !product.field_product_image;

  // Navigate to the product details route
const openProduct = (product) => {
  stateNavigator.navigate("productdetails", {
    category: slugify(product.field_category || "products"),
    productslug: slugify(product.title),
  });
};

  if (loading) {
    return (
      <section className="ts-wrap-outer">
        <p className="ts-status">Loading products...</p>
        <style>{`.ts-wrap-outer{width:100%;margin:60px auto;padding:0 40px;text-align:center;font-family:Arial,sans-serif;box-sizing:border-box;}`}</style>
      </section>
    );
  }

  if (error) {
    return (
      <section className="ts-wrap-outer">
        <p className="ts-status">Could not load products: {error}</p>
        <style>{`.ts-wrap-outer{width:100%;margin:60px auto;padding:0 40px;text-align:center;font-family:Arial,sans-serif;box-sizing:border-box;}`}</style>
      </section>
    );
  }

  return (
    <>
      <section className="top-sellers">
        <h2 className="ts-heading">Products</h2>

        <div className="ts-layout">
          <aside className="ts-categories">
            {categories.map((cat) => (
              <button
                key={cat}
                className={`ts-cat ${cat === activeCategory ? "active" : ""}`}
                onClick={() => setActiveCategory(cat)}
              >
                {cat}
              </button>
            ))}
          </aside>

          <div className="ts-carousel">
            <div className="ts-viewport">
              <div
                className="ts-track"
                style={{
                  transform: `translateX(-${(page * 100) / totalPages}%)`,
                  width: `${totalPages * 100}%`,
                }}
              >
                {Array.from({ length: totalPages }).map((_, pageIndex) => (
                  <div
                    className="ts-slide"
                    key={pageIndex}
                    style={{ width: `${100 / totalPages}%` }}
                  >
                    {currentProducts
                      .slice(
                        pageIndex * PRODUCTS_PER_PAGE,
                        (pageIndex + 1) * PRODUCTS_PER_PAGE
                      )
                      .map((p) => (
                        <div
                          className="ts-card"
                          key={p.nid}
                          onClick={() => openProduct(p)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              openProduct(p);
                            }
                          }}
                        >
                          <div className="ts-image">
                            <img
                              src={getImageUrl(p)}
                              alt={p.title}
                              className={isFallback(p) ? "ts-logo-fallback" : ""}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = FALLBACK_LOGO;
                                e.target.classList.add("ts-logo-fallback");
                              }}
                            />
                          </div>
                          <h3 className="ts-title">{p.title}</h3>
                          <p className="ts-price">
                            GH₵ {Number(p.field_price).toFixed(2)}
                          </p>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </div>

            {totalPages > 1 && (
              <>
                <button
                  className="ts-arrow ts-arrow-left"
                  onClick={() => goTo(page - 1)}
                  disabled={page === 0}
                  aria-label="Previous page"
                >
                  ‹
                </button>
                <button
                  className="ts-arrow ts-arrow-right"
                  onClick={() => goTo(page + 1)}
                  disabled={page >= totalPages - 1}
                  aria-label="Next page"
                >
                  ›
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      <style>{`
        .ts-wrap-outer {
          width: 100%;
          margin: 60px auto;
          padding: 0 40px;
          font-family: Arial, sans-serif;
          text-align: center;
          box-sizing: border-box;
        }

        .top-sellers {
          width: 100%;
          margin: 60px auto;
          padding: 0 40px;
          font-family: Arial, sans-serif;
          box-sizing: border-box;
        }

        .ts-heading {
          font-size: 28px;
          font-weight: 900;
          letter-spacing: 1px;
          color: #111;
          margin: 0 0 36px;
          text-align: center;
        }

        .ts-layout {
          display: grid;
          grid-template-columns: repeat(5, minmax(0, 1fr));
          gap: 20px;
          align-items: start;
          min-width: 0;
        }

        .ts-categories {
          grid-column: 1 / 2;
          display: flex;
          flex-direction: column;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          overflow: hidden;
          background: #fff;
          box-sizing: border-box;
          width: 100%;
        }
        .ts-cat {
          text-align: left;
          padding: 16px 20px;
          border: none;
          background: transparent;
          font-size: 15px;
          font-weight: 800;
          color: #333;
          cursor: pointer;
          border-bottom: 1px solid #f3f4f6;
          transition: all 0.15s ease;
          font-family: inherit;
          line-height: 1.3;
        }
        .ts-cat:last-child { border-bottom: none; }
        .ts-cat:hover { background: #f9fafb; color: #c8102e; }
        .ts-cat.active { background: #d1ac54; color: #000; }

        .ts-carousel {
          grid-column: 2 / 6;
          position: relative;
          min-width: 0;
          overflow: visible;
        }
        .ts-viewport {
          overflow: hidden;
          width: 100%;
        }
        .ts-track {
          display: flex;
          flex-wrap: nowrap;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          will-change: transform;
        }
        .ts-slide {
          flex: 0 0 auto;
          display: grid;
          grid-template-columns: repeat(4, minmax(0, 1fr));
          gap: 20px;
          box-sizing: border-box;
          overflow: hidden;
        }

        .ts-card {
          min-width: 0;
          border: 1px solid #e5e7eb;
          border-radius: 10px;
          padding: 16px;
          background: #fff;
          display: flex;
          flex-direction: column;
          transition: box-shadow 0.2s ease, transform 0.2s ease;
          box-sizing: border-box;
          overflow: hidden;
          cursor: pointer;
        }
        .ts-card:hover {
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
          transform: translateY(-2px);
        }
        .ts-card:focus-visible {
          outline: 3px solid #c8102e;
          outline-offset: 2px;
        }

        .ts-image {
          width: 100%;
          height: 240px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 14px;
          background: #f9fafb;
          border-radius: 8px;
          overflow: hidden;
          flex-shrink: 0;
          padding: 12px;
          box-sizing: border-box;
        }
        .ts-image img {
          max-width: 100%;
          max-height: 100%;
          width: auto;
          height: auto;
          object-fit: contain;
          display: block;
        }
        .ts-image img.ts-logo-fallback {
          max-width: 70%;
          max-height: 60%;
          opacity: 0.85;
        }

        .ts-title {
          font-size: 15px;
          font-weight: 600;
          color: #111;
          margin: 0 0 8px;
          line-height: 1.35;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          min-height: 40px;
        }

        .ts-price {
          font-size: 17px;
          font-weight: 700;
          color: #c8102e;
          margin: 0;
        }

        .ts-arrow {
          position: absolute;
          top: 50%;
          transform: translateY(-50%);
          width: 52px;
          height: 52px;
          border-radius: 50%;
          border: none;
          background: #c8102e;
          color: #fff;
          font-size: 32px;
          font-weight: 700;
          line-height: 1;
          cursor: pointer;
          box-shadow: 0 6px 16px rgba(200, 16, 46, 0.35);
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s ease;
          z-index: 5;
          padding-bottom: 3px;
        }
        .ts-arrow:hover:not(:disabled) {
          background: #111;
          transform: translateY(-50%) scale(1.1);
        }
        .ts-arrow:active:not(:disabled) {
          transform: translateY(-50%) scale(0.95);
        }
        .ts-arrow:disabled {
          opacity: 0.25;
          cursor: not-allowed;
          box-shadow: none;
        }
        .ts-arrow-left { left: 8px; }
        .ts-arrow-right { right: 8px; }

        .ts-status {
          text-align: center;
          color: #6b7280;
          padding: 40px 0;
        }

        @media (max-width: 1400px) {
          .ts-image { height: 200px; }
        }
        @media (max-width: 1100px) {
          .ts-image { height: 170px; }
          .ts-heading { font-size: 30px; }
          .ts-card { padding: 14px; }
          .ts-cat { padding: 14px 16px; font-size: 13px; }
        }
        @media (max-width: 850px) {
          .ts-layout {
            grid-template-columns: 1fr;
            gap: 16px;
          }
          .ts-categories {
            grid-column: 1 / -1;
            flex-direction: row;
            overflow-x: auto;
            overflow-y: hidden;
          }
          .ts-cat {
            white-space: nowrap;
            border-bottom: none;
            border-right: 1px solid #f3f4f6;
            padding: 14px 18px;
          }
          .ts-cat:last-child { border-right: none; }
          .ts-carousel { grid-column: 1 / -1; }
          .ts-slide {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 14px;
          }
          .ts-image { height: 150px; }
        }
        @media (max-width: 700px) {
          .top-sellers,
          .ts-wrap-outer {
            padding: 0 20px;
            margin: 40px auto;
          }
          .ts-heading { font-size: 26px; margin-bottom: 24px; }
          .ts-image { height: 130px; }
          .ts-arrow {
            width: 42px;
            height: 42px;
            font-size: 26px;
          }
          .ts-arrow-left { left: 4px; }
          .ts-arrow-right { right: 4px; }
        }
        @media (max-width: 500px) {
          .ts-heading { font-size: 22px; }
          .ts-card { padding: 12px; }
          .ts-image { height: 100px; }
          .ts-title { font-size: 13px; min-height: 34px; }
          .ts-price { font-size: 14px; }
          .ts-arrow {
            width: 36px;
            height: 36px;
            font-size: 22px;
          }
        }
      `}</style>
    </>
  );
}

export default TopSellers;